import {
  useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import { Button, MediaGallery, Modal } from '@components/common';
import { PlayIcon } from '@heroicons/react/20/solid';
import { VideoGalleryItem } from '@type/media-gallery';
import { clsx } from 'clsx';
import VideoThumbnail from '@root/src/components/common/Video/Thumbnail';
import { useExtendedRouter } from '@root/src/hooks/next-routing-wrappers';
import { route } from '@services/symfony';
import sendGoogleTagEvent from '@root/src/hooks/useSendGoogleTagEvent';
import { showCvContactModal } from '@root/src/components/features/modals/employer/CvContactModal';
import {
  ApiCheckUnlock,
  ApiCheckUnlockPay, fetcher, isApiV1Errors, isCheckUnlockStatusOk, isCheckUnlockStatusPayOk,
  ResponseWithHeaders,
} from '@root/src/utils';
import { showUpgradeSubscriptionModal } from '@root/src/components/features/modals/employer/subscription/UpgradeSubscriptionModal';
import { useStaticToasts } from '@root/src/hooks';
import { showAddCompanyInfoModal } from '@root/src/components/features/modals/employer/AddCompanyInfoModal';
import { useResumeAccessCheck } from '@components/templates/stories/hooks/useResumeAccesCheck';
import { CustomConfirmModalContent } from './CustomConfirmModalContent';
import { CvDetailObject } from '../../interface/CvDetailInterface';
import { InterfaceJobFolderOptions } from '../../sections/ProfileHeading';

interface VideoGalleryModalProps {
  cvDetail?: ApiSchema<'ResumeData'>;
  slug: string;
  mode: 'list' | 'button';
  isAuth?: boolean;
  locale?: Locale;
  revalidateCv?: (onSuccessCallback?: (cv: CvDetailObject) => void) => void;
  cvCurrentJobUnlockFolder: InterfaceJobFolderOptions;
  jobFolderInfo?:InterfaceJobFolderOptions;

}

export const VideoGalleryModal = (props: VideoGalleryModalProps) => {
  const { t } = useTranslation('common');
  const {
    cvDetail,
    mode = 'button',
    slug,
    isAuth = false,
    locale = 'ro',
    jobFolderInfo,
    revalidateCv,
    cvCurrentJobUnlockFolder,
  } = props;
  const { router } = useExtendedRouter();

  const [showAllVideos, setShowAllVideos] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
  const unlockCallbackRef = useRef<() => void>(null);
  const { status200Toast, showStatusToast } = useStaticToasts();
  const hasValidVideoUrl = (url?: string | null): url is string => typeof url === 'string' && url.trim().length > 0;

  // Unlock CV with interview
  // Will require some flow changes to have a common component for the unlock flow
  const unlockCvInterviews = () => {
    fetcher(`/v2/resume-operation/${slug}/check-unlock/contact`, locale, {
      method: 'POST',
      payload: { ...cvCurrentJobUnlockFolder },
      stringifyPayload: true,
      withResponseHeaders: true,
    }).then((res) => {
      const { status }: { status: number } = res as ResponseWithHeaders<ApiCheckUnlock>;
      const { response }: { response: ApiCheckUnlock } = res as ResponseWithHeaders<ApiCheckUnlock>;

      if (status === 402) {
        const { response: cartCreateResponseId }: { response: ApiCheckUnlockPay } = res as ResponseWithHeaders<ApiCheckUnlockPay>;
        if (isCheckUnlockStatusPayOk(cartCreateResponseId)) {
          showCvContactModal(cartCreateResponseId?.id, locale);
        }
        return;
      }

      if (isCheckUnlockStatusOk(response)) {
        sendGoogleTagEvent('premium_contact_interview', 'Custom');
        if (revalidateCv) {
          revalidateCv((newCvDetail) => {
            if (newCvDetail?.locked === false) {
              unlockCallbackRef.current?.();
              unlockCallbackRef.current = null;
            }
          });
        }
        setShowConfirmModal(false);
        if (response.meta?.consumptionInfoSummary) {
          status200Toast(response.meta.consumptionInfoSummary);
        }
      } else if (isApiV1Errors(response)) {
        const { code } = response.errors[0];
        if (code === 402) {
          if (isCheckUnlockStatusPayOk(response)) {
            setShowConfirmModal(false);
            showCvContactModal(response?.id, locale);
          }
          return;
        }

        if (code === 403 || code === 701) {
          showAddCompanyInfoModal(locale, unlockCvInterviews);
          return;
        }

        // If the error status code is 601 (not enough credits) we show the upgrade subscription modal.
        // Otherwise, we show a toast with the error message.
        if (code === 601) {
          showUpgradeSubscriptionModal();
        } else {
          showStatusToast(String(`${code}`));
          setShowConfirmModal(false);
        }
      }
    }).catch(() => {});
  };

  // Build video list
  const videos = useMemo(() => {
    const videoList: VideoGalleryItem[] = [];

    cvDetail?.jobInterviewVideos?.forEach((video) => {
      if (!hasValidVideoUrl(video.url)) return;
      videoList.push({
        type: 'video',
        src: video.url,
        caption: video.question,
        trackingMetadata: [
          { key: 'questionId', value: video.questionId?.toString() ?? '' },
          { key: 'videoId', value: video.id },
          { key: 'jobSlug', value: video.jobSlug ?? '' },
          { key: 'jobTitle', value: video.jobTitle ?? '' },
        ],
      });
    });

    cvDetail?.interviewVideos?.forEach((video) => {
      if (!hasValidVideoUrl(video.url)) return;
      videoList.push({
        type: 'video',
        src: video.url,
        caption: video.question,
        trackingMetadata: [
          { key: 'questionId', value: video.questionId?.toString() ?? '' },
          { key: 'videoId', value: video.id },
          { key: 'jobSlug', value: video.jobSlug ?? '' },
          { key: 'jobTitle', value: video.jobTitle ?? '' },
        ],
      });
    });

    if (hasValidVideoUrl(cvDetail?.privateInformation?.videoUrl)) {
      videoList.push({ type: 'video', src: cvDetail.privateInformation.videoUrl });
    }

    return videoList;
  }, [cvDetail]);


  const { isAllowed: isAllowedPlayingVideo, check: checkResumeAccess } = useResumeAccessCheck(locale, slug);

  const openIfAllowed = async (
    index: number,
    openGalleryAtIndex: (i: number) => void,
  ) => {
    if (!isAuth) {
      void router.push(
        route('login', {
          _target_path: encodeURIComponent(`/${locale}/cv/${slug}`),
        }),
      );
      return;
    }

    if (isAllowedPlayingVideo) {
      openGalleryAtIndex(index);
      return;
    }

    let allowed = false;
    try {
      allowed = await checkResumeAccess(slug);
    } catch {
      allowed = false;
    }

    if (!allowed) {
      setShowConfirmModal(true);
      return;
    }

    openGalleryAtIndex(index);
  };


  if (!videos.length) return null;

  return (
    <>
      <MediaGallery
        items={videos}
        withThumbnailNav
        onSlideChange={() => { sendGoogleTagEvent('viewed_interview', 'Custom', { source: 'video_gallery' }); }}
        trackingEventData={{
          eventType: 'view',
          entityType: 'resume',
          entityId: slug,
          action: 'interview',
          viewContext: 'detail',
        }}
      >
        {(openGalleryAtIndex) => (
          <>
            {mode === 'button' && (
              <Button
                styling="solid"
                rounding="full"
                color="white"
                onClick={() => openGalleryAtIndex(0)}
                className="items-center gap-1 px-3 py-1 text-tiny font-bold tracking-widest"
              >
                <PlayIcon className="size-4 shrink-0" />
                <span className="shrink-0">
                  {cvDetail?.privateInformation?.interviewVideos?.length
                  || cvDetail?.jobInterviewVideos?.length
                    ? t('ai.interview.cv.badge')
                    : t('profile.similar.has.video.cv')}
                </span>
              </Button>
            )}

            {mode === 'list' && (
              <div className="rounded-sm bg-surface-100/30 px-3 py-5">
                <h3 className="mb-3 grow text-md font-bold">
                  {t('cv.details.bestie.interviews')}
                </h3>
                <div className="flex max-h-80 flex-col gap-4 overflow-y-auto">
                  {(showAllVideos ? videos : videos.slice(0, 2)).map(
                    (item, index) => (
                      <div key={`video-gallery-item-${index + 1}`} className="flex items-center">
                        <h3 className="line-clamp-3 grow pr-2 text-sm">
                          {item.caption}
                        </h3>
                        <Button
                          styling="none"
                          className={clsx(
                            'relative inline-flex size-20 h-14 min-w-20 shrink-0 items-center justify-center overflow-hidden rounded-md opacity-80 transition-opacity hover:opacity-100',
                          )}
                          onClick={() => {
                            void openIfAllowed(index, openGalleryAtIndex);
                          }}
                        >
                          <VideoThumbnail src={item.src} shouldShowDuration />
                        </Button>
                      </div>
                    ),
                  )}
                </div>
                {!showAllVideos && videos.length > 2 && (
                  <div
                    role="presentation"
                    onClick={() => setShowAllVideos(true)}
                    className="mx-auto mt-5 flex w-fit cursor-pointer items-center rounded-full bg-surface-100 px-4 py-2 text-center font-bold"
                  >
                    {`${t('global.label.see.all.interviews')} +${videos.length - 3}`}
                  </div>
                )}
              </div>
            )}
            <Modal
              size="sm"
              isOpen={showConfirmModal}
              body={(
                <CustomConfirmModalContent
                  jobContextDetail={{
                    jobFolderId: jobFolderInfo?.jobId ?? 0,
                    title: jobFolderInfo?.title ?? '',
                    jobSlug: jobFolderInfo?.jobSlug ?? '',
                  }}
                  onSendSuccess={(val) => {
                    if (val) {
                      void unlockCvInterviews();
                    } else {
                      setShowConfirmModal(false);
                    }
                  }}
                  userSlug={slug ?? ''}
                />
              )}
              onClose={() => { setShowConfirmModal(false); }}
            />
          </>
        )}
      </MediaGallery>
    </>
  );
};
